import { useParams, Link } from "react-router-dom";
import {
  useIncident,
  useUpdateIncidentStatus,
  useAssignIncident,
  useUsers,
  useIncidentComments,
  useAddIncidentComment,
  useNearbyWorkers,
} from "@crm/api";
import {
  ArrowLeft,
  User,
  Clock,
  AlertCircle,
  CheckCircle,
  Send,
  MapPin,
  Map,
} from "lucide-react";
import { cn } from "@crm/utils";
import { toast } from "sonner";
import { useState, lazy, Suspense } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { NearbyWorkers } from "../../../components/NearbyWorkers";
import { IncidentChat } from "../components/IncidentChat";

// Lazy load map component to avoid SSR issues
const WorkerMap = lazy(() =>
  import("../../../components/maps/WorkerMap").then((m) => ({
    default: m.WorkerMap,
  }))
);

const commentSchema = z.object({
  content: z.string().min(1, "Comment cannot be empty"),
  is_internal: z.boolean().default(false),
});

type CommentFormData = z.infer<typeof commentSchema>;

const statusConfig: Record<
  string,
  { label: string; color: string; icon: typeof AlertCircle }
> = {
  new: { label: "New", color: "bg-blue-100 text-blue-800", icon: AlertCircle },
  assigned: {
    label: "Assigned",
    color: "bg-yellow-100 text-yellow-800",
    icon: Clock,
  },
  in_progress: {
    label: "In Progress",
    color: "bg-purple-100 text-purple-800",
    icon: Clock,
  },
  on_hold: {
    label: "On Hold",
    color: "bg-gray-100 text-gray-800",
    icon: Clock,
  },
  resolved: {
    label: "Resolved",
    color: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
  closed: {
    label: "Closed",
    color: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-100 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700" },
  high: { label: "High", color: "bg-orange-100 text-orange-700" },
  critical: { label: "Critical", color: "bg-red-100 text-red-700" },
};

const categoryLabels: Record<string, string> = {
  hardware: "Hardware",
  software: "Software",
  network: "Network",
  security: "Security",
  access: "Access Request",
  other: "Other",
};

export function IncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: incident, isLoading, error } = useIncident(id!);
  const { data: comments, isLoading: commentsLoading } = useIncidentComments(
    id!
  );
  const { data: usersData } = useUsers();
  const { data: nearbyWorkers } = useNearbyWorkers(id!, { enabled: !!id });
  const updateStatusMutation = useUpdateIncidentStatus();
  const assignMutation = useAssignIncident();
  const addCommentMutation = useAddIncidentComment();

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      content: "",
      is_internal: false,
    },
  });

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    try {
      await updateStatusMutation.mutateAsync({ id, status: newStatus });
      toast.success("Status updated");
      setShowStatusDropdown(false);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleAssign = async (userId: string | null) => {
    if (!id) return;
    try {
      await assignMutation.mutateAsync({ id, assigneeId: userId });
      toast.success(userId ? "Incident assigned" : "Incident unassigned");
      setShowAssignDropdown(false);
    } catch {
      toast.error("Failed to assign incident");
    }
  };

  const onSubmitComment = async (data: CommentFormData) => {
    if (!id) return;
    try {
      await addCommentMutation.mutateAsync({
        incidentId: id,
        payload: data,
      });
      toast.success("Comment added");
      reset();
    } catch {
      toast.error("Failed to add comment");
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="p-8">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">Failed to load incident</p>
          <Link
            to="/incidents"
            className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Incidents
          </Link>
        </div>
      </div>
    );
  }

  const status = statusConfig[incident.status] || statusConfig.new;
  const priority = priorityConfig[incident.priority] || priorityConfig.medium;
  // Staff members who can be assigned (technicians, managers, admins)
  const assignableStaff =
    usersData?.results?.filter((u) => u.role !== "client") || [];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/incidents"
          className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Incidents
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {incident.title}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              ID: {incident.id.slice(0, 8)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1 text-sm font-medium",
                priority.color
              )}
            >
              {priority.label}
            </span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              Description
            </h2>
            <p className="whitespace-pre-wrap text-foreground">
              {incident.description || "No description provided."}
            </p>
          </div>

          {/* Resolution Notes */}
          {incident.resolution_notes && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-6">
              <h2 className="mb-4 text-lg font-semibold text-green-800">
                Resolution Notes
              </h2>
              <p className="whitespace-pre-wrap text-green-700">
                {incident.resolution_notes}
              </p>
            </div>
          )}

          {/* Comments Section */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              Comments & Updates
            </h2>

            {/* Comment Form */}
            <form onSubmit={handleSubmit(onSubmitComment)} className="mb-6">
              <div className="space-y-3">
                <textarea
                  {...register("content")}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  placeholder="Add a comment..."
                />
                {errors.content && (
                  <p className="text-sm text-destructive">
                    {errors.content.message}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      {...register("is_internal")}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Internal note (staff only)
                  </label>
                  <button
                    type="submit"
                    disabled={addCommentMutation.isPending}
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                    Add Comment
                  </button>
                </div>
              </div>
            </form>

            {/* Comments List */}
            {commentsLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : !comments || comments.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={cn(
                      "rounded-lg border p-4",
                      comment.is_internal
                        ? "border-yellow-200 bg-yellow-50"
                        : "border-border bg-muted/50"
                    )}
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                          {comment.author?.first_name?.[0] || "?"}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {comment.author?.full_name || "Unknown"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(comment.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {comment.is_internal && (
                        <span className="rounded-full bg-yellow-200 px-2 py-0.5 text-xs font-medium text-yellow-800">
                          Internal
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-foreground">
                      {comment.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="mb-4 text-sm font-semibold text-foreground">
              Status
            </h3>
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium",
                  status.color
                )}
              >
                {status.label}
                <Clock className="h-4 w-4" />
              </button>
              {showStatusDropdown && (
                <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-md border border-border bg-card shadow-lg">
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => handleStatusChange(key)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted"
                      disabled={updateStatusMutation.isPending}
                    >
                      {config.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Assignment */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="mb-4 text-sm font-semibold text-foreground">
              Assigned To
            </h3>
            <div className="relative">
              <button
                onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                className="flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <User className="h-4 w-4 text-muted-foreground" />
                {incident.assigned_to
                  ? `${incident.assigned_to.first_name} ${incident.assigned_to.last_name}`
                  : "Unassigned"}
              </button>
              {showAssignDropdown && (
                <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-md border border-border bg-card shadow-lg">
                  <button
                    onClick={() => handleAssign(null)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
                    disabled={assignMutation.isPending}
                  >
                    Unassigned
                  </button>
                  {assignableStaff.map((staff) => (
                    <button
                      key={staff.id}
                      onClick={() => handleAssign(staff.id)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted"
                      disabled={assignMutation.isPending}
                    >
                      {staff.first_name} {staff.last_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="mb-4 text-sm font-semibold text-foreground">
              Details
            </h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Category</dt>
                <dd className="font-medium text-foreground">
                  {categoryLabels[incident.category] || incident.category}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd className="font-medium text-foreground">
                  {new Date(incident.created_at).toLocaleString()}
                </dd>
              </div>
              {incident.resolved_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Resolved</dt>
                  <dd className="font-medium text-foreground">
                    {new Date(incident.resolved_at).toLocaleString()}
                  </dd>
                </div>
              )}
              {incident.client_company && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Client</dt>
                  <dd className="font-medium text-foreground">
                    {incident.client_company.name}
                  </dd>
                </div>
              )}
              {(incident.location_address || incident.location_latitude) && (
                <div className="pt-2 border-t border-border">
                  <dt className="text-muted-foreground flex items-center gap-1 mb-1">
                    <MapPin className="h-3 w-3" />
                    Location
                  </dt>
                  <dd className="text-sm">
                    {incident.location_address && (
                      <p className="font-medium text-foreground">
                        {incident.location_address}
                      </p>
                    )}
                    {incident.location_latitude &&
                      incident.location_longitude && (
                        <>
                          <p className="text-xs text-muted-foreground">
                            {Number(incident.location_latitude).toFixed(6)},{" "}
                            {Number(incident.location_longitude).toFixed(6)}
                          </p>
                          <button
                            onClick={() => setShowMap(!showMap)}
                            className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <Map className="h-3 w-3" />
                            {showMap ? "Hide Map" : "Show on Map"}
                          </button>
                        </>
                      )}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Map View */}
          {showMap &&
            incident.location_latitude &&
            incident.location_longitude && (
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="border-b border-border bg-muted/50 px-4 py-2">
                  <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Map className="h-4 w-4" />
                    Workers & Incident Map
                  </h3>
                </div>
                <Suspense
                  fallback={
                    <div className="h-[350px] flex items-center justify-center bg-muted/20">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  }
                >
                  <WorkerMap
                    workers={nearbyWorkers?.workers || []}
                    incidentLocation={{
                      latitude: Number(incident.location_latitude),
                      longitude: Number(incident.location_longitude),
                    }}
                    incidentAddress={incident.location_address || undefined}
                    onWorkerClick={(worker) => handleAssign(worker.user_id)}
                    className="h-[350px] w-full"
                  />
                </Suspense>
              </div>
            )}

          {/* Nearby Workers */}
          <NearbyWorkers
            incidentId={id!}
            onAssign={(userId) => handleAssign(userId)}
            isAssigning={assignMutation.isPending}
          />

          {/* Real-time Chat */}
          <IncidentChat incidentId={id!} />
        </div>
      </div>
    </div>
  );
}
